import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'ui/links';
import { Caption } from 'ui/theme';
import { PageActions } from './PageActions';
import { Cell, ProjectCell, ProjectName } from './UsagePages.styled';
import { useBillingQueryContext } from '../../contexts';
import { JVT_COUNT, ORG_ROW_NAME, PROJECT_NAME_COLUMN_VALUE } from '../../constants';
import { createColumns, createRows, queryByFn } from '../../utils';
import { SimpleIndexPage } from '../SimpleIndexPage/SimpleIndexPage';
import { getJourneyEventsUsageForProjectLink } from '../Index/journeyEvents/JourneyEventsCard';

export const TEXT = {
  PAGE_TITLE: 'Journey events usage',
};

export const JourneyEventsUsage = () => {
  const {
    projectIds,
    pickManyProjectMetrics,
    pickManyOrgMetrics,
    projectMetricsByProjectId,
    isLoading,
  } = useBillingQueryContext();
  const { search } = useLocation();

  useEffect(() => {
    window.scrollTo();
  }, []);

  const metricNames = [JVT_COUNT];

  const orgMetrics = pickManyOrgMetrics(metricNames);
  const metricsByProjectId = projectIds.reduce(
    (acc, projectId) => ({
      [projectId]: pickManyProjectMetrics(projectId, metricNames),
      ...acc,
    }),
    {},
  );

  const rows = createRows(orgMetrics, projectMetricsByProjectId);
  /* display the projects with the highest usage first */
  rows.sort(
    (a, b) => (b.JvtCount ? (b.JvtCount as number) : 0) - (a.JvtCount ? (a.JvtCount as number) : 0),
  );
  const columns = createColumns(metricsByProjectId);

  return (
    <SimpleIndexPage
      rows={rows}
      columns={columns}
      createRowCell={createRow}
      breadcrumb={{
        content: 'Usage and Billing',
        href: `/payments/info${search}`,
      }}
      pageTitle={TEXT.PAGE_TITLE}
      pageActions={<PageActions />}
      isLoading={isLoading}
      queryByFn={queryByFn}
    />
  );
};

type CellProps = {
  isOrgRow: boolean;
  row: any;
  colValue: string;
};

const TableCell = ({ isOrgRow, row, colValue }: CellProps) => {
  return colValue === PROJECT_NAME_COLUMN_VALUE ? (
    <ProjectCell isOrg={isOrgRow}>
      <ProjectName>
        {!isOrgRow && (
          <Link
            data-test="project-name-link"
            useReactRouter
            href={getJourneyEventsUsageForProjectLink(row.projectId)}
          >
            {row.projectName}
          </Link>
        )}
        {isOrgRow && ORG_ROW_NAME}
      </ProjectName>
      {/* projectId is set to '' in case of an Organization metric */}
      {row.projectId && <Caption>Project ID: {row.projectId}</Caption>}
    </ProjectCell>
  ) : (
    <Cell isOrg={isOrgRow}>{row[colValue]?.toLocaleString()}</Cell>
  );
};

export const createRow =
  (colValue: string) =>
  ({ row }: { row: Record<string, string | number> }) => (
    <TableCell isOrgRow={!row.projectId} row={row} colValue={colValue} />
  );
